import React, { useState, useEffect } from 'react';
import { X, Edit3, Loader2, AlertTriangle } from 'lucide-react';
import { instrumentAPI } from '../../../../../services/api/instrumentAPI';

const ModifyTradeModal = ({
  isOpen,
  onClose,
  onConfirm,
  trade,
  processing,
  formatNumber,
}) => {
  const [takeProfit, setTakeProfit] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [quantity, setQuantity] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [error, setError] = useState('');

  // Instrument constraints
  const [minSize, setMinSize] = useState(null);
  const [maxSize, setMaxSize] = useState(null);
  const [qtyStep, setQtyStep] = useState(null);
  const [priceDecimals, setPriceDecimals] = useState(null);
  const [constraintsLoading, setConstraintsLoading] = useState(false);

  const normalPrice = (val) => val != null ? String(+Number(val).toFixed(8)) : '';

  useEffect(() => {
    if (trade && isOpen) {
      setTakeProfit(trade.takeProfit != null ? formatNumber(trade.takeProfit, 5).replace(/,/g, '') : '');
      setStopLoss(trade.stopLoss != null ? formatNumber(trade.stopLoss, 5).replace(/,/g, '') : '');
      setQuantity(trade.quantity != null ? String(trade.quantity) : '');
      setEntryPrice(normalPrice(trade.entryPrice));
      setTimestamp('');
      setTimestamp('');
      setError('');
      setMinSize(null);
      setMaxSize(null);
      setQtyStep(null);
      fetchInstrumentConstraints(trade.symbol);
    }
  }, [trade, isOpen]);

  const fetchInstrumentConstraints = async (symbol) => {
    try {
      setConstraintsLoading(true);
      const response = await instrumentAPI.getAll();
      const list = response?.data?.data?.data || response?.data?.data || response?.data || [];
      const instruments = Array.isArray(list) ? list : [];
      const inst = instruments.find(i => i.symbol === symbol);
      if (inst) {
        const min = parseFloat(inst.min_Order_Size) || 0;
        const max = parseFloat(inst.max_Order_Size) || 0;
        const step = parseFloat(inst.order_Quantity_Step) || 0;
        const decimals = parseInt(inst.decimals, 10);
        setMinSize(min > 0 ? min : null);
        setMaxSize(max > 0 ? max : null);
        setQtyStep(step > 0 ? step : null);
        setPriceDecimals(!isNaN(decimals) && decimals >= 0 ? decimals : null);
      } else {
        setMinSize(null);
        setMaxSize(null);
        setQtyStep(null);
        setPriceDecimals(null);
      }
    } catch {
      setMinSize(null);
      setMaxSize(null);
      setQtyStep(null);
      setPriceDecimals(null);
    } finally {
      setConstraintsLoading(false);
    }
  };

  if (!isOpen || !trade) return null;

  const decimals = priceDecimals != null ? priceDecimals : 5;
  const priceStep = priceDecimals != null ? String(Math.pow(10, -priceDecimals)) : 'any';

  const validate = () => {
    const newQty = quantity !== '' ? Number(quantity) : undefined;
    const newTP = takeProfit !== '' ? Number(takeProfit) : undefined;
    const newSL = stopLoss !== '' ? Number(stopLoss) : undefined;
    const newEntry = entryPrice !== '' ? Number(entryPrice) : undefined;

    if (newQty !== undefined && (newQty <= 0 || !isFinite(newQty))) return 'Quantity must be positive';
    if (newTP !== undefined && (newTP <= 0 || !isFinite(newTP))) return 'Take Profit must be positive';
    if (newSL !== undefined && (newSL <= 0 || !isFinite(newSL))) return 'Stop Loss must be positive';
    if (newEntry !== undefined && (newEntry <= 0 || !isFinite(newEntry))) return 'Entry price must be positive';

    // Min / max check
    if (newQty !== undefined) {
      if (minSize != null && newQty < minSize) return 'Quantity cannot be below minimum (' + formatNumber(minSize, 2) + ')';
      if (maxSize != null && newQty > maxSize) return 'Quantity cannot exceed maximum (' + formatNumber(maxSize, 2) + ')';
      // Step / multiplier check
      if (qtyStep != null && qtyStep > 0 && minSize != null) {
        const remainder = (newQty - minSize) % qtyStep;
        if (Math.abs(remainder) > 0.0001) {
          const nearestBelow = minSize + Math.floor((newQty - minSize) / qtyStep) * qtyStep;
          const nearestAbove = nearestBelow + qtyStep;
          return 'Quantity must be in increments of ' + formatNumber(qtyStep, 2) + '. Nearest valid: ' + formatNumber(nearestBelow, 2) + ' or ' + formatNumber(nearestAbove, 2);
        }
      }
    }

    const origQty = String(trade.quantity ?? '');
    const origTP = trade.takeProfit != null ? String(trade.takeProfit) : '';
    const origSL = trade.stopLoss != null ? String(trade.stopLoss) : '';
    const origEntry = normalPrice(trade.entryPrice);

    if (takeProfit === origTP && stopLoss === origSL && quantity === origQty && entryPrice === origEntry) {
      return 'No changes detected';
    }
    return null;
  };

  const handleConfirm = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const params = {};
    const origQty = String(trade.quantity ?? '');
    const origTP = trade.takeProfit != null ? String(trade.takeProfit) : '';
    const origSL = trade.stopLoss != null ? String(trade.stopLoss) : '';
    const origEntry = normalPrice(trade.entryPrice);
    if (takeProfit !== origTP) params.takeProfit = takeProfit !== '' ? +Number(takeProfit).toFixed(decimals) : null;
    if (stopLoss !== origSL) params.stopLoss = stopLoss !== '' ? +Number(stopLoss).toFixed(decimals) : null;
    if (quantity !== origQty) params.quantity = Number(quantity);
    if (entryPrice !== origEntry) params.entryPrice = +Number(entryPrice).toFixed(decimals);

    onConfirm({
      tradeId: trade.tradeId,
      userId: trade.userId,
      timestamp: timestamp || undefined,
      ...params,
    });
  };

  const hasChanges = () => {
    const origQty = String(trade.quantity ?? '');
    const origTP = trade.takeProfit != null ? String(trade.takeProfit) : '';
    const origSL = trade.stopLoss != null ? String(trade.stopLoss) : '';
    const origEntry = normalPrice(trade.entryPrice);
    return takeProfit !== origTP || stopLoss !== origSL || quantity !== origQty || entryPrice !== origEntry || !!timestamp;
  };

  // Live step warning for quantity field
  const newQty = quantity !== '' ? Number(quantity) : NaN;
  const qtyChanged = quantity !== String(trade.quantity ?? '');
  const violatesStep = qtyChanged && !isNaN(newQty) && qtyStep != null && qtyStep > 0 && minSize != null
    && Math.abs((newQty - minSize) % qtyStep) > 0.0001;
  const violatesMin = qtyChanged && !isNaN(newQty) && minSize != null && newQty < minSize;

  // Constraint hint
  const constraintHint = () => {
    if (minSize == null) return '';
    let hint = 'Min: ' + formatNumber(minSize, 2);
    if (maxSize != null) hint += ' | Max: ' + formatNumber(maxSize, 2);
    if (qtyStep != null) hint += ' | Step: ' + formatNumber(qtyStep, 2);
    return hint;
  };

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h3><Edit3 size={15} /> Modify {trade.symbol}</h3>
          <button className="pm-modal-close" onClick={onClose} disabled={processing}>
            <X size={14} />
          </button>
        </div>

        <div className="pm-modal-body">
          <div className="pm-modal-strip">
            <span className={'pm-tag pm-tag--' + trade.side}>
              {trade.side === 'buy' ? 'LONG' : 'SHORT'}
            </span>
            <span className="pm-num" style={{ fontSize: '0.8rem' }}>{formatNumber(trade.quantity, 2)}</span>
            <span style={{ color: 'var(--pm-text-muted)', fontSize: '0.75rem' }}>@</span>
            <span className="pm-num" style={{ fontSize: '0.75rem', color: 'var(--pm-text-sec)' }}>{formatNumber(trade.entryPrice, decimals)}</span>
          </div>

          {/* Instrument constraints info */}
          {!constraintsLoading && constraintHint() && (
            <div style={{ fontSize: '0.65rem', color: 'var(--pm-text-muted)', marginBottom: '0.4rem', padding: '4px 8px', background: 'var(--pm-bg)', borderRadius: 'var(--pm-r-sm)' }}>
              {constraintHint()}
            </div>
          )}

          <div className="pm-modal-field">
            <label>
              Quantity
              {constraintHint() && (
                <span style={{ fontWeight: 400, fontSize: '0.62rem', color: 'var(--pm-text-muted)', marginLeft: '6px' }}>
                  ({constraintHint()})
                </span>
              )}
            </label>
            <input
              type="number"
              min={minSize || 0}
              max={maxSize || undefined}
              step={qtyStep || 'any'}
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); setError(''); }}
              disabled={processing}
            />
          </div>

          {/* Live step warning */}
          {(violatesStep || violatesMin) && (
            <div className="pm-warning" style={{ marginTop: '-0.2rem', marginBottom: '0.4rem' }}>
              <AlertTriangle size={12} />
              <span>
                {violatesMin
                  ? 'Quantity must be at least ' + formatNumber(minSize, 2)
                  : 'Quantity must be in increments of ' + formatNumber(qtyStep, 2) + ' (e.g. ' + formatNumber(minSize, 2) + ', ' + formatNumber(minSize + qtyStep, 2) + ', ' + formatNumber(minSize + qtyStep * 2, 2) + ')'
                }
              </span>
            </div>
          )}

          <div className="pm-modal-field">
            <label>Entry Price</label>
            <input
              type="number"
              min="0"
              step={priceStep}
              value={entryPrice}
              onChange={(e) => { setEntryPrice(e.target.value); setError(''); }}
              placeholder={trade.entryPrice != null ? 'Current: ' + formatNumber(trade.entryPrice, decimals) : 'Set entry price...'}
              disabled={processing}
            />
          </div>

          <div className="pm-modal-row">
            <div className="pm-modal-field" style={{ marginBottom: 0 }}>
              <label>
                Take Profit
                {takeProfit === '' && trade.takeProfit != null && (
                  <span style={{ fontWeight: 400, fontSize: '0.62rem', color: '#d97706', marginLeft: '4px' }}>
                    (will be removed)
                  </span>
                )}
              </label>
              <input
                type="number"
                min="0"
                step={priceStep}
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                placeholder={trade.takeProfit != null ? 'Current: ' + formatNumber(trade.takeProfit, decimals) : 'Set TP...'}
                disabled={processing}
              />
            </div>
            <div className="pm-modal-field" style={{ marginBottom: 0 }}>
              <label>
                Stop Loss
                {stopLoss === '' && trade.stopLoss != null && (
                  <span style={{ fontWeight: 400, fontSize: '0.62rem', color: '#d97706', marginLeft: '4px' }}>
                    (will be removed)
                  </span>
                )}
              </label>
              <input
                type="number"
                min="0"
                step={priceStep}
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                placeholder={trade.stopLoss != null ? 'Current: ' + formatNumber(trade.stopLoss, decimals) : 'Set SL...'}
                disabled={processing}
              />
            </div>
          </div>

          <div className="pm-modal-field">
            <label>
              Timestamp <span style={{ fontWeight: 400, fontSize: '0.65rem', color: 'var(--pm-text-muted)' }}>(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              disabled={processing}
            />
          </div>

          {qtyChanged && !violatesStep && !violatesMin && (
            <div className="pm-warning">
              <AlertTriangle size={12} />
              <span>Modifying quantity will update the position size directly. Margin adjustments may occur.</span>
            </div>
          )}

          {error && (
            <div className="pm-warning" style={{ marginTop: '0.4rem' }}>
              <AlertTriangle size={12} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="pm-modal-footer">
          <button className="pm-btn pm-btn--ghost" onClick={onClose} disabled={processing} style={{ fontSize: '0.78rem' }}>
            Cancel
          </button>
          <button
            className="pm-btn pm-btn--primary"
            onClick={handleConfirm}
            disabled={processing || !hasChanges()}
            style={{ fontSize: '0.78rem' }}
          >
            {processing ? (
              <><Loader2 size={12} className="pm-spin" /> Saving</>
            ) : (
              <><Edit3 size={12} /> Save Changes</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModifyTradeModal;
